import type { VoiceHoldBarLabels } from '../components/voice-hold-bar/voice-hold-bar.component';
import type { VoiceHoldButtonLabels } from '../components/voice-hold-button/voice-hold-button.component';
import type { VoiceLockedPanelLabels } from '../components/voice-locked-panel/voice-locked-panel.component';
import type { HoldDictationState } from './live-dictation.model';

/** Le etichette dei tre pezzi della dettatura tenuta premuta: pulsante, barra di registrazione, pannello. */
export interface HoldDictationLabels {
  readonly button: VoiceHoldButtonLabels;
  readonly bar: VoiceHoldBarLabels;
  readonly panel: VoiceLockedPanelLabels;
}

/**
 * La dettatura tenuta premuta come la vede chi la mostra — la barra della conversazione, il campo della
 * richiesta al motore: lo stato del gesto e cio' che serve a disegnarlo, gia' tradotto.
 */
export interface HoldDictationView {
  readonly state: HoldDictationState;
  /** Dettatura bloccata col microfono chiuso: l'onda si ferma e la pausa diventa ripresa. */
  readonly paused: boolean;
  /**
   * Il microfono non apre un'altra dettatura: l'ultima sta arrivando, o ne e' aperta una da un altro
   * campo della stessa pagina.
   */
  readonly busy: boolean;
  /**
   * La dettatura bloccata offre «Modifica», che mette il testo nel campo invece di mandarlo. La chiede la
   * postazione Operatore; il pannello la nasconde comunque sotto i 1024px.
   */
  readonly editable: boolean;
  /** Lingua da destra a sinistra: l'annullamento si fa scorrendo verso destra. */
  readonly rtl: boolean;
  readonly elapsedMs: number;
  readonly waveform: readonly number[];
  /** Perche' l'ultima dettatura non e' partita, gia' tradotto. */
  readonly error: string | null;
  readonly labels: HoldDictationLabels;
}

/**
 * Cio' che il gesto chiede, detto con una parola sola: un campo annidato in un pannello lo riporta con un
 * solo output invece di otto, e la pagina lo traduce in una chiamata a `HOLD_DICTATION_HANDLER`.
 */
export type HoldDictationCommand =
  'start' | 'release' | 'lock' | 'cancel' | 'pause' | 'resume' | 'send' | 'edit';

/**
 * Le stesse chiavi per il Cittadino e per la postazione: a tradurle e' la pagina, che passa la propria
 * `translate` perche' il testo si ricalcoli al cambio lingua dentro il suo `computed()`.
 */
export function holdDictationLabels(translate: (key: string) => string): HoldDictationLabels {
  const recording = translate('voiceMessage.states.recording');
  const elapsedAriaLabel = translate('voiceMessage.modal.elapsedAriaLabel');
  return {
    button: { ariaLabel: translate('voiceMessage.recordButton.ariaLabel') },
    bar: {
      slideToCancel: translate('voiceMessage.hold.slideToCancel'),
      recording,
      elapsedAriaLabel,
    },
    panel: {
      title: translate('voiceMessage.modal.title'),
      pause: translate('voiceMessage.locked.pause'),
      resume: translate('voiceMessage.modal.resume'),
      edit: translate('voiceMessage.modal.edit'),
      send: translate('voiceMessage.modal.send'),
      cancel: translate('voiceMessage.modal.cancel'),
      recording,
      paused: translate('voiceMessage.states.paused'),
      elapsedAriaLabel,
    },
  };
}
